// frontend/src/lib/api.js

const ENV_BACKEND =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_BACKEND_URL) ||
  (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) ||
  "https://cybercheck-1.onrender.com";

export const API_BASE = String(ENV_BACKEND).replace(/\/+$/, "");

export function normalizeDomain(input) {
  if (!input) return "";
  let d = String(input).trim().toLowerCase();
  d = d.replace(/^https?:\/\//, "").replace(/^www\./, "");
  d = d.split("/")[0].split("?")[0].split("#")[0].split(":")[0];
  return d.replace(/\.+$/, "");
}

export function isValidDomain(d) {
  if (!d || d.length > 253) return false;
  return /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(d);
}

export class ApiError extends Error {
  constructor(message, { status, retryAfter, requestId, kind } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = retryAfter;
    this.requestId = requestId;
    this.kind = kind;
  }
}

async function readJsonSafely(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    return { ok: false, reason: `non-json (${ct || "unknown"})` };
  }
  try {
    return { ok: true, data: await res.json() };
  } catch (e) {
    return { ok: false, reason: `invalid json: ${e.message}` };
  }
}

async function httpError(res) {
  const parsed = await readJsonSafely(res);
  const detail =
    (parsed.ok && parsed.data && parsed.data.detail) ||
    `Server returned ${res.status}`;
  return new ApiError(detail, {
    status: res.status,
    retryAfter: Number(res.headers.get("retry-after")) || undefined,
    requestId: res.headers.get("x-request-id") || undefined,
    kind: "http",
  });
}

export async function scanDomain(domain, { signal } = {}) {
  if (!API_BASE) {
    throw new ApiError(
      "Backend URL is not configured. Set VITE_BACKEND_URL (Vite) or REACT_APP_BACKEND_URL (CRA) and rebuild.",
      { kind: "protocol" }
    );
  }

  const url = `${API_BASE}/api/scan`;

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ domain }),
      signal,
    });
  } catch (err) {
    if (err.name === "AbortError") throw err;
    throw new ApiError(
      `Could not reach the scanner at ${API_BASE}. ${err.message}`,
      { kind: "network" }
    );
  }

  if (!res.ok) throw await httpError(res);

  const parsed = await readJsonSafely(res);
  if (!parsed.ok) {
    throw new ApiError(
      `Scanner returned an unexpected response (${parsed.reason}). ` +
        `Check that ${API_BASE} is running the CyberCheck backend.`,
      { status: res.status, kind: "protocol" }
    );
  }

  return { ...parsed.data, source: "live" };
}

export async function summariseCategory(category) {
  if (!API_BASE) {
    throw new ApiError("Backend URL is not configured.", { kind: "protocol" });
  }

  const payload = {
    label: String(category.label || "").slice(0, 120),
    score: Number.isFinite(category.score) ? category.score : 0,
    findings: (category.findings || []).slice(0, 40).map((f) => ({
      id: String(f.id || "").slice(0, 80),
      title: String(f.title || "").slice(0, 200),
      severity: String(f.severity || "info").slice(0, 20),
      what: String(f.what || "").slice(0, 500),
      why: String(f.why || "").slice(0, 500),
      fix: String(f.fix || "").slice(0, 500),
    })),
  };

  let res;
  try {
    res = await fetch(`${API_BASE}/api/summarise`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw new ApiError(
      `Could not reach the summariser. ${err.message}`,
      { kind: "network" }
    );
  }

  if (!res.ok) throw await httpError(res);

  const parsed = await readJsonSafely(res);
  if (!parsed.ok || !parsed.data || typeof parsed.data.summary !== "string") {
    throw new ApiError("Summariser returned an unexpected response.",
                       { kind: "protocol" });
  }

  return parsed.data.summary;
}

/**
 * Fetch a fresh, plain-English explanation of what CyberCheck is and
 * why it matters. Every call reaches DeepSeek — no cached or mock text
 * on the frontend side.
 */
export async function fetchAboutSummary() {
  if (!API_BASE) {
    throw new ApiError("Backend URL is not configured.", { kind: "protocol" });
  }

  let res;
  try {
    res = await fetch(`${API_BASE}/api/about-summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({}),
      // Explicitly bypass any intermediary cache.
      cache: "no-store",
    });
  } catch (err) {
    throw new ApiError(
      `Could not reach the summariser. ${err.message}`,
      { kind: "network" }
    );
  }

  if (!res.ok) throw await httpError(res);

  const parsed = await readJsonSafely(res);
  if (!parsed.ok || !parsed.data || typeof parsed.data.summary !== "string") {
    throw new ApiError("Summariser returned an unexpected response.",
                       { kind: "protocol" });
  }

  return parsed.data.summary;
}

export async function listMessages(limit = 50) {
  if (!API_BASE) return [];
  const res = await fetch(`${API_BASE}/api/messages?limit=${limit}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw await httpError(res);
  const parsed = await readJsonSafely(res);
  return parsed.ok ? parsed.data : [];
}

export async function createMessage(content) {
  if (!API_BASE) {
    throw new ApiError("Backend URL is not configured.", { kind: "protocol" });
  }
  const res = await fetch(`${API_BASE}/api/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ content }),
  });
  if (!res.ok) throw await httpError(res);
  const parsed = await readJsonSafely(res);
  if (!parsed.ok) throw new ApiError("Unexpected response from server.");
  return parsed.data;
}